import { Phone, MessageCircle, CalendarCheck, MapPin, MessageSquare, Mail, Clock, ArrowUpRight, Star } from "lucide-react";
import { Link } from "react-router-dom";
import { BRAND, CONTACT } from "../../lib/landingData";
import { CTAButton } from "./CTAButton";
import { LogoMark } from "./LogoMark";
import { useLang } from "../../i18n/LanguageContext";

const Row = ({ icon: Icon, children, testId }) => (
  <li className="flex items-start gap-4" data-testid={testId}>
    <Icon className="h-5 w-5 text-cream/70 shrink-0 mt-1" strokeWidth={1.6} />
    <div className="min-w-0">{children}</div>
  </li>
);

export const Footer = () => {
  const { t } = useLang();
  const b = t.brand.footer;
  return (
    <footer className="relative bg-green text-cream">
      <div className="mx-auto max-w-[1400px] px-5 sm:px-8 py-20 sm:py-24">
        <div className="grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <div className="flex items-center gap-2.5">
              <LogoMark tone="white" className="h-11 w-auto shrink-0" testId="footer-logo-mark" />
              <div className="leading-tight">
                <span className="font-display text-xl sm:text-2xl leading-none block" data-testid="footer-brand">{BRAND.name}</span>
                <span className="text-[11px] uppercase tracking-[0.2em] text-cream/60">{t.brand.tagline}</span>
              </div>
            </div>
            <p className="mt-6 max-w-md text-cream/70 leading-relaxed">{t.footer.blurb}</p>
            <div className="mt-8">
              <CTAButton testId="footer-cta" label={t.footer.cta} variant="invert" />
            </div>
            <a
              href={BRAND.taxSite}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="footer-tax-link"
              className="mt-6 inline-flex items-center gap-1.5 text-sm text-cream/80 underline underline-offset-4 hover:text-cream transition-colors"
            >
              {b.taxQueries} <ArrowUpRight className="h-3.5 w-3.5" />
            </a>
          </div>

          <div className="lg:col-span-4 lg:pl-6">
            <span className="text-xs uppercase tracking-[0.25em] text-cream/70 font-semibold">{t.footer.getInTouch}</span>
            <ul className="mt-6 space-y-5">
              <Row icon={Mail}>
                <a href={CONTACT.emailHref} data-testid="footer-email" className="text-lg text-cream hover:text-cream/70 transition-colors break-all">{CONTACT.email}</a>
              </Row>
              <Row icon={Phone}>
                <a href={CONTACT.phoneHref} data-testid="footer-phone" className="text-lg text-cream hover:text-cream/70 transition-colors">{CONTACT.phone}</a>
              </Row>
              <Row icon={MessageCircle}>
                <a href={CONTACT.whatsappChat} target="_blank" rel="noopener noreferrer" data-testid="footer-whatsapp" className="text-lg text-cream hover:text-cream/70 transition-colors">{t.footer.whatsapp}</a>
              </Row>
              <Row icon={MessageSquare} testId="footer-wechat">
                <span className="text-lg text-cream/90">{t.footer.wechat}</span>
              </Row>
              <Row icon={CalendarCheck}>
                <a href={CONTACT.booking} target="_blank" rel="noopener noreferrer" data-testid="footer-booking" className="text-lg text-cream hover:text-cream/70 transition-colors">{t.footer.booking}</a>
              </Row>
            </ul>
          </div>

          <div className="lg:col-span-3">
            <span className="text-xs uppercase tracking-[0.25em] text-cream/70 font-semibold">{b.address}</span>
            <ul className="mt-6 space-y-5">
              <Row icon={MapPin} testId="footer-address">
                <span className="text-base text-cream/85 leading-relaxed">{CONTACT.address}</span>
              </Row>
              <Row icon={Clock} testId="footer-hours">
                <span className="block text-[11px] uppercase tracking-[0.2em] text-cream/60">{b.hours}</span>
                <span className="text-base text-cream/85 leading-relaxed">{CONTACT.hours}</span>
              </Row>
              <Row icon={Star}>
                <a href={CONTACT.reviewUrl} target="_blank" rel="noopener noreferrer" data-testid="footer-review" className="text-base text-cream underline underline-offset-4 hover:text-cream/70 transition-colors">{t.brand.review}</a>
              </Row>
            </ul>
          </div>
        </div>

        <div className="mt-16 pt-8 border-t border-cream/15 flex flex-col gap-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <p className="text-sm text-cream/60" data-testid="footer-legal-line">{b.legal}</p>
            <div className="flex items-center gap-5">
              <Link to="/privacy" data-testid="footer-privacy" className="text-sm text-cream/70 underline underline-offset-2 hover:text-cream transition-colors">
                Privacy Policy
              </Link>
              <Link to="/terms" data-testid="footer-terms" className="text-sm text-cream/70 underline underline-offset-2 hover:text-cream transition-colors">
                Terms of Service
              </Link>
            </div>
          </div>
          <p className="text-sm text-cream/40" data-testid="footer-copyright">© {new Date().getFullYear()} {BRAND.name}. {t.footer.rights}</p>
        </div>
      </div>
    </footer>
  );
};
